<template>
  <v-slide-x-reverse-transition>
    <v-card class="elevation-5 provider-group-details vop-bg-whisper" v-show="showXpathDetails">
      <div>
        <v-toolbar flat class="vop-bg-whisper">
          <v-toolbar-title class="subheading ttu font-weight-medium">
            <span v-if="isEditMode"> {{ messages.label.EDIT_XPATH_MAPPING }}</span>
            <span v-else>{{ messages.label.ADD_XPATH_MAPPING }}</span>
          </v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="hide()">
            <v-icon medium>close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-card-text>
          <v-layout wrap>
            <v-flex xs12 v-if="isEditMode">
              <v-alert :value="true" class="black--text text--lighten-2" type="warning">
                Changes to 'XPath Location' do not change XPath value for existing conditions or actions using 'this'
                XPath
                Mapping.
              </v-alert>
            </v-flex>
            <v-flex xs12 v-if="xpathMappingError !== ''">
              <v-alert :value="true" type="error">
                {{ xpathMappingError }}
              </v-alert>
            </v-flex>
            <v-flex xs12>
              <v-form>
                <!-- Title -->
                <v-flex xs11>
                  <v-text-field v-model="xpathMapping.title" :clearable="!isEditMode" :loading="loadingXPathMapping"
                    :disabled="loadingXPathMapping"
                    v-validate="{ required: true, max: 500, regex: /^[0-9a-zA-Z\s\/_-]*$/i }"
                    :error-messages="errors.collect('title')" data-vv-name="title" data-vv-delay="500" :counter="500"
                    :label="messages.label.TITLE" required>
                  </v-text-field>
                </v-flex>
                <!-- Xpath Mapping Location -->
                <v-flex xs11>
                  <v-textarea v-model="xpathMapping.xpathLocation" :clearable="!isEditMode"
                    :loading="loadingXPathMapping" :disabled="loadingXPathMapping" v-validate="'required|max:3000'"
                    :error-messages="errors.collect('xpathLocation')" data-vv-name="xpathLocation" data-vv-delay="500"
                    :counter="3000" :label="messages.label.XPATH_LOCATION" required>
                  </v-textarea>
                </v-flex>
                <!-- scheme -->
                <v-flex xs11>
                  <v-select v-model="xpathMapping.scheme" :label="messages.label.SCHEME" :items="schemeList"
                    item-text="text" item-value="value"></v-select>
                </v-flex>
              </v-form>
            </v-flex>
          </v-layout>
        </v-card-text>
      </div>
      <v-card-actions class="px-3">
        <v-spacer />
        <!-- Delete Xpath Mapping template -->
        <v-dialog v-model="deleteDialog" width="500" v-if="isEditMode">
          <v-btn color="error" slot="activator">
            {{ messages.label.DELETE }}
          </v-btn>
          <v-card>
            <v-card-title class="subheading ttu">
              {{ messages.label.DELETE_XPATH_MAPPING }}
            </v-card-title>
            <v-card-text>
              <p>{{ messages.info.DELETE_XPATH_1 }} <b>{{ selectedXpathKey.key }}</b> ? </p>
              <p v-html="messages.info.DELETE_XPATH_2"></p>
            </v-card-text>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn color="grey lighten-1 white--text" @click="deleteDialog = false">
                {{ messages.label.CANCEL }}
              </v-btn>
              <v-btn color="error" :disabled="deletingXPathMapping" :loading="deletingXPathMapping"
                @click="deleteXPathMapping(selectedXpathKey.key)">
                Yes
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <v-btn color="primary" class="ml-2" :disabled="updatingXPathMapping" :loading="updatingXPathMapping"
          v-if="isEditMode" @click="updateXPathMapping">
          {{ messages.label.SAVE }}
        </v-btn>
        <v-btn color="primary" class="ml-2" :disabled="addingXPathMapping" :loading="addingXPathMapping"
          @click="addXPathMapping" v-else>
          {{ messages.label.ADD }}
        </v-btn>
      </v-card-actions>
      <!-- Snackbar -->
      <v-flex xs12>
        <v-snackbar :bottom="true" :timeout="snackbar.timeout" :color="snackbar.color" v-model="snackbar.show">
          {{ snackbar.message }}
          <v-btn flat color="secondary" @click.native="snackbar.show = false">
            {{ messages.label.OK }}
          </v-btn>
        </v-snackbar>
      </v-flex>
    </v-card>
  </v-slide-x-reverse-transition>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex"
import messages from "@/XVAT/messages.json"
import * as xvatRulesDataService from "@/XVAT/XVATDataService"
import { GET_XPATH_MAPPINGS_LIST, GET_XPATH_MAPPING_KEYS } from "@/store/constants/action-types"
import { SET_SELECTED_XPATH_KEY, SET_SHOW_XPATH_DETAILS } from "@/store/constants/mutation-types"

/**
 * initialData
 */
function initialData(instance) {
  return {
    xpathMapping: {},
    xpathMappingError: "",
    loadingXPathMapping: false,
    addingXPathMapping: false,
    updatingXPathMapping: false,
    deletingXPathMapping: false,
    deleteDialog: false,
    schemeList: instance.getSchemeList(),
    dictionary: instance.getValidationDictionary(),
    snackbar: {
      show: false,
      timeout: 5000,
      message: "",
      color: "error"
    }
  }
}

export default {
  name: "XPathDetails",
  $_veeValidate: {
    validator: "new"
  },
  data() {
    return initialData(this)
  },
  computed: {
    ...mapState("xvat", [
      "showXpathDetails",
      "selectedXpathKey",
      "xpathMappingKeysList"
    ]),
    isEditMode() {
      return !this.$lodash.isEmpty(this.selectedXpathKey)
    }
  },
  watch: {
    selectedXpathKey: {
      handler(newVal, oldVal) {
        if (newVal !== oldVal) {
          this.showDetailsView()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations("xvat", [
      SET_SELECTED_XPATH_KEY,
      SET_SHOW_XPATH_DETAILS
    ]),

    ...mapActions("xvat", [
      GET_XPATH_MAPPINGS_LIST,
      GET_XPATH_MAPPING_KEYS
    ]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
    },

    /**
     * Hide side sheet view
     */
    hide() {
      this.SET_SELECTED_XPATH_KEY({})
      this.SET_SHOW_XPATH_DETAILS(false)
      this.resetXPathMppingData()
    },

    /**
    * getRuleStates
    * @returns {array}
    */
    getSchemeList() {
      return [
        { text: messages.label.SCHEME_ADI11, value: "ADI 1.1" },
        { text: messages.label.SCHEME_PROVIDER_LIST, value: "ProviderList" },
      ]
    },

    /**
     * Show Details view and update data
     */
    showDetailsView() {
      this.resetXPathMppingData()
      if (this.showXpathDetails && !this.$lodash.isEmpty(this.selectedXpathKey)) {
        //Edit Mode
        this.getXPathMappingDetails(this.selectedXpathKey.key)
      } else if (this.showXpathDetails && this.$lodash.isEmpty(this.selectedXpathKey)) {
        // Create Mode
        this.xpathMapping = {
          scheme: "ADI 1.1"
        }
      }
    },

    /**
     * Retreive XPath Mapping details
     */
    getXPathMappingDetails(xpathKey) {
      this.loadingXPathMapping = true
      xvatRulesDataService
        .getXPathMapping(xpathKey)
        .then((response) => {
          this.xpathMapping = response.data
        })
        .catch((error) => {
          let response = error.response.data
          if (response.isException) {
            this.xpathMappingError = response.description
          }
        }).finally(() => {
          this.loadingXPathMapping = false
        })
    },

    /**
     * Validation messages for vee-validate
     */
    getValidationDictionary() {
      return {
        custom: {
          title: {
            required: () => "Title can't be empty",
            max: "Title may not be greater than 500 characters",
            regex: "Valid characters are : A-Z a-z 0-9 / _ -"
          },
          xpathLocation: {
            required: () => "XPath Location can't be empty",
            max: "XPath Location may not be greater than 3000 characters"
          }
        }
      }
    },

    /**
     * Re-initialize
     */
    resetXPathMppingData() {
      this.reset()
      this.$validator.reset()
    },

    /**
     * Add new xpath mapping
     */
    addXPathMapping() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.addingXPathMapping = true
          //Create key from title
          let xpathKey = this.$lodash.replace(this.xpathMapping.title, /[^a-zA-Z0-9]/g, "_")
          xpathKey = this.$lodash.trim(xpathKey)
          xvatRulesDataService
            .createXPathMapping(xpathKey, this.xpathMapping)
            .then((response) => {
              // Snack bar
              this.snackbar.show = true
              this.snackbar.color = "success"
              this.snackbar.message = this.messages.info.XPATH_CREATE_SUCCESS

              return xpathKey
            })
            .then((xpathKey) => {
              this.GET_XPATH_MAPPING_KEYS().then(() => {
                this.updateSelection(xpathKey)
              })
            })
            .catch((error) => {
              if (error.response.status == 409) {
                //TODO: Response doesn't come back in json structure (APIFIX)
                this.xpathMappingError = "Failed to add item '" + xpathKey + "' already exists"
              } else {
                let response = error.response.data
                if (response.isException) {
                  this.xpathMappingError = response.description
                }
              }
              // return Promise.reject(error)
            })
            .finally(() => {
              this.addingXPathMapping = false
              this.GET_XPATH_MAPPINGS_LIST()// Update store
            })
        }
      })
    },

    /**
     * Update selection in list view
     * @param {*} xpathMappingKey
     */
    updateSelection(xpathMappingKey) {
      if (!xpathMappingKey) return

      let newSelection = this.$lodash.find(
        this.xpathMappingKeysList,
        item => {
          return item.key === xpathMappingKey
        }
      )
      if (newSelection) {
        this.SET_SELECTED_XPATH_KEY(newSelection)
      }
    },

    /**
    * Update XpathMapping Wrapper
    * Check for Title change and delete current mappings
    */
    updateXPathMapping() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          this.xpathMappingError = ""
          let xpathKeyNew = this.$lodash.replace(this.xpathMapping.title, /[^a-zA-Z0-9]/g, "_")
          if (this.selectedXpathKey.key !== xpathKeyNew) {
            this.updatingXPathMapping = true
            //NOTE : When title has changed, we are going through this process
            // 1. Create new one with new key
            // 2. Delete old key
            xvatRulesDataService
              .createXPathMapping(xpathKeyNew, this.xpathMapping)
              .then(() => {
                return xvatRulesDataService.deleteXPathMapping(this.selectedXpathKey.key)
                  .then(() => {
                    return xpathKeyNew
                  })
                  .catch((error) => {
                    return Promise.reject(error)
                  })
              })
              .then((xpathKeyNew) => {
                this.GET_XPATH_MAPPING_KEYS().then(() => {
                  this.updateSelection(xpathKeyNew)
                })
              })
              .then(() => {
                this.GET_XPATH_MAPPINGS_LIST()// Update store
              })
              .catch((error) => {
                if (error.response.status == 409) {
                  //TODO: Response doesn't come back in json structure (APIFIX)
                  this.xpathMappingError = "Failed to update item '" + xpathKeyNew + "' already exists"
                } else {
                  let response = error.response.data
                  if (response.isException) {
                    this.xpathMappingError = response.description
                  }
                }
                // return Promise.reject(error)
              }).finally(() => {
                this.updatingXPathMapping = false

              })
          } else {
            this.updatingXPathMapping = true
            xvatRulesDataService
              .updateXPathMapping(this.selectedXpathKey.key, this.xpathMapping)
              .then((response) => {
                // Snackbar
                this.snackbar.show = true
                this.snackbar.color = "success"
                this.snackbar.message = this.messages.info.XPATH_UPDATE_SUCCESS

                // return response
              })
              .catch((error) => {
                let response = error.response.data
                if (response.isException) {
                  this.xpathMappingError = response.description
                }
                // return Promise.reject(error)
              })
              .finally(() => {
                this.updatingXPathMapping = false
                this.GET_XPATH_MAPPINGS_LIST()// Update store
              })
          }
        }
      })
    },

    /**
     * Delete provider group
     */
    deleteXPathMapping(xpathKey) {
      this.deletingXPathMapping = true
      xvatRulesDataService
        .deleteXPathMapping(xpathKey)
        .then((response) => {
          this.SET_SELECTED_XPATH_KEY({})
          this.SET_SHOW_XPATH_DETAILS(false)

          // Snackbar
          this.snackbar.show = true
          this.snackbar.color = "success"
          this.snackbar.message = this.messages.info.XPATH_DELETE_SUCCESS

          // return response
        })
        .catch((error) => {
          let response = error.response.data
          if (response.isException) {
            this.xpathMappingError = response.description
          }
          // return Promise.reject(error)
        })
        .finally(() => {
          this.deleteDialog = false
          this.deletingXPathMapping = false
          this.GET_XPATH_MAPPINGS_LIST()// Update Store
          this.GET_XPATH_MAPPING_KEYS()
        })
    }
  },
  mounted() {
    this.$validator.localize("en", this.dictionary)
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages
    /**
     * Events
     */
  },
  beforeDestroy() {
  }
}
</script>

<style lang="scss">
.provider-group-details {
  min-height: 50%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
</style>



// WEBPACK FOOTER //
// src/XVAT/XpathMapping/XPathDetails.vue